import { classNames } from '@fullstory/util/fmt';
import React, { HTMLAttributes, ReactNode } from 'react';
import { SpaceScale } from '../../styled-system/types/scale';
import { identifyComponent } from '../../util';
import { GridItem } from './GridItem';
import css, { ClassNames } from './index.module.scss';

export interface GridLayoutItem {
  width: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  height: number;
}

export type GridLayout = GridLayoutItem[];

export interface GridProps extends HTMLAttributes<HTMLDivElement> {
  testId?: string;
  /** The grid layout, which is a list of layout dimensions for the grid children */
  layout: GridLayout;
  /**
   * Render the item for a given index of the `layout` array. Provides the dimensions
   * of the item to render in case it is useful.
   */
  renderItem: (index: number, dimensions: GridLayoutItem) => ReactNode;
  /** Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64 */
  gridGap?: SpaceScale;
  /** Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64 */
  gridColumnGap?: SpaceScale;
  /** Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64 */
  gridRowGap?: SpaceScale;
}

export const Grid = React.forwardRef<HTMLDivElement, GridProps>(function Grid(
  {
    testId = 'grid',
    layout,
    renderItem,
    className,
    gridGap = 0,
    gridColumnGap = 0,
    gridRowGap = 0,
  },
  ref,
) {
  let gridColumnGapClassName: string;
  let gridRowGapClassName: string;
  if (gridGap > 0) {
    gridColumnGapClassName = css[`gcg_${gridGap}px` as ClassNames];
    gridRowGapClassName = css[`grg_${gridGap}px` as ClassNames];
  } else {
    gridColumnGapClassName = css[`gcg_${gridColumnGap}px` as ClassNames];
    gridRowGapClassName = css[`grg_${gridRowGap}px` as ClassNames];
  }
  return (
    <div
      data-component="Grid"
      data-testid={testId}
      ref={ref}
      className={classNames(
        className,
        css.gridComponent,
        gridColumnGapClassName,
        gridRowGapClassName,
      )}
      {...identifyComponent('grid')}
    >
      {layout.map((dimensions, itemIndex) => {
        // In most cases, the layout object probably will not be needed, but we do surface it
        // in case it is useful in some scenario.
        const child = renderItem(itemIndex, dimensions);
        let gridItemKey: React.Key = itemIndex;
        if (React.isValidElement(child) && child.key !== null) {
          gridItemKey = child.key;
        }

        // Apply a width to the grid item for printing
        const gridItemWidthClassName = css[`gridItemWidth_${dimensions.width}` as ClassNames];
        return (
          <GridItem key={gridItemKey} {...dimensions} className={gridItemWidthClassName}>
            {child}
          </GridItem>
        );
      })}
    </div>
  );
});
